// import { destroyZoom } from '../ProductImages';

/**
 * PDPVideo - Main class for handling product videos in the product display page
 */

const VIDEO_EVENTS = {
  INIT_READY: 'pdp-video:init-ready',
  SLIDES_SYNCED: 'pdp-video:slides-synced',
  CAROUSELS_UPDATED: 'pdp-video:carousels-updated',
  QUICKVIEW_VIDEO_READY: 'pdp-video:quickview-ready',
  VIDEO_VISIBILITY_UPDATED: 'pdp-video:video-visibility-updated',
};

class PDPVideo {
  // Static property to track initialized contexts
  static handlersInitialized = new Set();
  static VIDEO_EVENTS = VIDEO_EVENTS;

  /**
   * Constructor for PDPVideo class
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.context - Context element (document for PDP, modal element for QuickView)
   * @param {Object} options.videoData - Video data for the product
   * @param {Object} options.productData - Product data
   */
  constructor(options = {}) {
    this.context = options.context || document;
    this.videoData = null;
    this.productData = null;
    this.currentVariantBarcode = null;
    this.vimeoPlayers = [];
    this.mainCarousel = this.context.querySelector('[data-product-images-carousel]');
    this.thumbsCarousel = this.context.querySelector('[data-product-images-thumbs-wrapper]');
    this.videoElements = [];
    this.initialized = false;
    this.mainSwiper = this.mainCarousel ? this.mainCarousel.querySelector('.swiper-main') : null;
    this.mainSwiperWrapper = this.mainSwiper ? this.mainSwiper.querySelector('.swiper-wrapper') : null;
    this.thumbsSwiper = this.thumbsCarousel ? this.thumbsCarousel.querySelector('.swiper') : null;
    this.thumbsSwiperWrapper = this.thumbsSwiper ? this.thumbsSwiper.querySelector('.swiper-wrapper') : null;
    this.videoDataElement = this.context.querySelector('[data-product-video-data]');
    this.productJsonElement = this.context.querySelector('[data-product-json]');

    // Reset state if this is a QuickView instance
    if (this.context !== document) {
      this.reset();
      // Listen for QuickView close to cleanup
      this.context.addEventListener('quickViewCleanup', () => this.cleanup());
    }

    // Initialize if we have the necessary elements
    if (this.videoDataElement) {
      this.init(this.context);
    } else {
      const observer = new MutationObserver((mutations, obs) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            this.videoDataElement = this.context.querySelector('script[data-product-video-data]');
            if (this.videoDataElement) {
              obs.disconnect();
              this.init(this.context);
              this.context.dispatchEvent(new CustomEvent(VIDEO_EVENTS.INIT_READY));
            }
          }
        });
      });

      observer.observe(this.context, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Reset video state
   * This is important when opening a new QuickView to clear previous variant state
   */
  reset() {
    this.currentVariantBarcode = null;
    this.videoData = null;
    this.productData = null;
    this.initialized = false;

    // Hide all video slides in this context
    const allVideoSlides = this.context.querySelectorAll('.swiper-slide[data-video-variant-barcode]');
    allVideoSlides.forEach((slide) => {
      slide.style.display = 'none';
    });
  }

  /**
   * Clean up resources when QuickView closes
   */
  cleanup() {
    // Remove all video slides
    const allVideoSlides = this.context.querySelectorAll('.swiper-slide[data-video-variant-barcode]');
    allVideoSlides.forEach((slide) => slide.remove());

    // Cleanup Vimeo players
    this.vimeoPlayers.forEach((player) => {
      if (player && typeof player.destroy === 'function') {
        player.destroy();
      }
    });
    this.vimeoPlayers = [];

    // Reset state
    this.reset();
  }

  /**
   * Initialize the component
   * @param {HTMLElement} context - Context element
   */
  init(context) {
    // If already initialized for this context, don't initialize again
    if (this.initialized && this.context === context) {
      return;
    }

    try {
      // Get fresh video data from the DOM
      const videoJson = this.videoDataElement ? this.videoDataElement.textContent.trim() : '';
      if (!videoJson) {
        return;
      }

      // Parse fresh video data
      this.videoData = JSON.parse(videoJson);
      if (!this.videoData || !this.videoData.videos || this.videoData.videos.length === 0) {
        return;
      }

      // Get fresh product data
      if (this.productJsonElement) {
        const productJson = this.productJsonElement.textContent.trim();
        if (productJson) {
          this.productData = JSON.parse(productJson);
        }
      }

      // Set up event listeners only once per context
      if (!PDPVideo.handlersInitialized.has(context)) {
        this.setupEventListeners();
        PDPVideo.handlersInitialized.add(context);
      }

      // Get the current variant barcode
      const variantBarcode = this.getCurrentVariantBarcode();
      this.currentVariantBarcode = variantBarcode;

      // Process videos for the current variant
      if (variantBarcode) {
        this.processVideosForVariant(variantBarcode);
        this.checkAndAddVideosToDom(null, this.context);
        this.updateVideoVisibility(variantBarcode, this.context);
      } else {
        // No specific variant selected, add all videos
        this.checkAndAddVideosToDom(this.videoData.videos, this.context);
      }

      // Inline (Liquid-rendered) video slides don't go through CarouselManager,
      // so they need an observer attached here for pause-on-exit behavior.
      this.observeInlineVideoSlides();

      this.initialized = true;
    } catch (error) {
      console.error('Error initializing PDPVideo:', error);
    }
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Handle play/pause button clicks
    this.setupPlayPauseHandlers();
  }

  /**
   * Set up play/pause handlers for videos
   */
  setupPlayPauseHandlers() {
    // Use a static class property to keep track of which containers already have handlers
    if (PDPVideo.handlersInitialized && PDPVideo.handlersInitialized.has(this.context)) {
      return; // Handlers already set up for this context
    }

    // Mark this context as initialized
    PDPVideo.handlersInitialized.add(this.context);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') return;

      const el = document.querySelector('#playPauseButton');
      if (!el) return;

      const video = el.closest('.swiper-slide')?.querySelector('video');
      if (!video) return;
      if (el.ariaLabel !== 'Pause video') return;

      // Resume playback only — do NOT call video.load(), which would re-issue
      // a network request to Cloudinary on every tab refocus.
      video.play().catch(() => {});
    });

    // Use this.context instead of document to scope the listener to the current container
    this.context.addEventListener('click', (e) => {
      // Handle play/pause button clicks
      const el = e.target.closest('#playPauseButton');
      if (el) {
        e.stopPropagation(); // Prevent event from bubbling up
        this.togglePlayPause(el);
      }
    });
  }

  /**
   * Toggle play/pause state of a video
   * @param {HTMLElement} button - The play/pause button
   */
  // eslint-disable-next-line no-unused-vars
  togglePlayPause(button) {}

  /**
   * Initialize Vimeo players for external videos
   * @returns {Array} - Array of initialized Vimeo player instances
   */
  initializeVimeoPlayers() {}

  /**
   * Get the metafield filename for a variant
   * @param {string} barcode - Variant barcode
   * @returns {string|null} - Metafield filename or null
   */
  // eslint-disable-next-line no-unused-vars
  getMetafieldFilename(barcode) {}

  /**
   * Process videos for a variant
   * @param {string} barcode - Variant barcode
   */
  processVideosForVariant(barcode) {
    if (!barcode || !this.videoData) return;

    // Check if this variant has any videos
    const hasVideos = this.videoData.barcodes.includes(barcode);
    if (!hasVideos) {
      // If no videos for this variant, hide all videos
      const allVideoSlides = this.context.querySelectorAll('.swiper-slide[data-video-variant-barcode]');
      allVideoSlides.forEach((slide) => {
        slide.style.display = 'none';
      });
      return;
    }

    // Get videos for this variant
    const variantVideos = this.videoData.videos.filter((video) => video.barcode === barcode);

    // Update visibility
    this.updateVideoVisibility(barcode);

    // Check if videos need to be added to DOM
    this.checkAndAddVideosToDom(variantVideos);
  }

  /**
   * Update video visibility for a specific variant
   * @param {string} variantBarcode - Barcode of the current variant
   */
  updateVideoVisibility(variantBarcode) {
    if (!variantBarcode || !this.videoData) return;

    // First hide all videos in this context
    const allVideoSlides = this.context.querySelectorAll('.swiper-slide[data-video-variant-barcode]');
    allVideoSlides.forEach((slide) => {
      slide.style.display = 'none';
    });

    // Only show videos that match this variant's barcode
    const matchingVideos = this.videoData.videos.filter((video) => video.barcode === variantBarcode);
    if (matchingVideos.length > 0) {
      const videoSlides = this.context.querySelectorAll(
        `.swiper-slide[data-video-variant-barcode="${variantBarcode}"]`,
      );
      videoSlides.forEach((slide) => {
        slide.style.display = '';
      });
    }
  }

  /**
   * Check and add videos to the DOM
   * @param {Array|HTMLElement} videos - Array of video objects or context element
   * @param {HTMLElement} context - Context element
   */
  // eslint-disable-next-line no-unused-vars
  checkAndAddVideosToDom(videos, context) {}

  /**
   * Add a video to the main carousel
   * @param {Object} video - Video object
   */
  // eslint-disable-next-line no-unused-vars
  addVideoMainSlide(video) {}

  /**
   * Add a video thumbnail to the thumbs carousel
   * @param {Object} video - Video object
   */
  // eslint-disable-next-line no-unused-vars
  addVideoThumbnail(video) {}

  /**
   * Synchronize videos between carousels
   * @param {string} variantBarcode - Barcode of the current variant
   */
  // eslint-disable-next-line no-unused-vars
  syncVideosBetweenCarousels(variantBarcode) {}

  /**
   * Update carousels after changing slides
   */
  updateCarousels() {}

  /**
   * Verify and fix video slides after variant change
   * @param {string} variantBarcode - Barcode of the current variant
   */
  verifyVideoSlidesAfterVariantChange(variantBarcode) {
    // Implement the logic to verify and fix video slides after variant change
    if (variantBarcode) {
      // Process the variant barcode - this uses the parameter to fix the linting error
      this.processVideosForVariant(variantBarcode);
    }
  }

  /**
   * Get the current variant barcode
   * @returns {string|null} The barcode of the current variant
   */
  getCurrentVariantBarcode() {
    // Try to get from ProductOptions data attribute first
    const productOptions = this.context.querySelector('.ProductOptions');
    if (productOptions && productOptions.getAttribute('data-variant-barcode')) {
      const barcode = productOptions.getAttribute('data-variant-barcode');
      return barcode;
    }

    // Try to get it from the variant ID element
    const variantIdElement = this.context.querySelector('[data-variant-id]');
    if (variantIdElement) {
      const variantId = variantIdElement.getAttribute('data-variant-id');
      if (this.productData && this.productData.variants) {
        const currentVariant = this.productData.variants.find((v) => v.id.toString() === variantId);
        if (currentVariant && currentVariant.barcode) {
          return currentVariant.barcode;
        }
      }
    }

    // Try alternative method to get current variant
    const styleNumberValue = this.context.querySelector('[data-style-number-value]');
    if (styleNumberValue && styleNumberValue.textContent) {
      return styleNumberValue.textContent.trim();
    }

    // Try one more time with a different selector
    const variantIdElement2 = this.context.querySelector('[data-variant-id]');
    if (variantIdElement2 && this.productData && this.productData.variants) {
      const variantId = variantIdElement2.getAttribute('data-variant-id');
      const currentVariant = this.productData.variants.find((v) => v.id.toString() === variantId);
      if (currentVariant && currentVariant.barcode) {
        return currentVariant.barcode;
      }
    }

    // If we have a stored barcode, use that
    if (this.currentVariantBarcode) {
      return this.currentVariantBarcode;
    }

    return null;
  }
}

export default PDPVideo;

// Make PDPVideo available globally
if (typeof window !== 'undefined') {
  window.PDPVideo = PDPVideo;
}
